import { useCallback, useEffect, useState } from 'react';
import { MailCheck, Truck } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { translateStatus } from './utils/StojkiAutoEmail';

const API_BASE_URL = '/racks-emails';

const StojkiAutoEmail = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'data';
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [customerEmails, setCustomerEmails] = useState([]);
  const [emailPreviews, setEmailPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('metal_stands');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreviews, setShowPreviews] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNoteEmail, setEditingNoteEmail] = useState(null);
  const [noteModalValue, setNoteModalValue] = useState('');
  const [csvErrors, setCsvErrors] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());

  const loadSessions = useCallback(async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/sessions?limit=50`);
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  }, []);

  const loadSessionData = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    try {
      const emailsResponse = await api.get(`${API_BASE_URL}/sessions/${sessionId}/emails`);
      setCustomerEmails(emailsResponse.data.customer_emails || []);
    } catch (err) {
      console.error('Error loading session data:', err);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/templates`);
      setTemplates(response.data.templates || {});
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    }
  }, []);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
    loadTemplates();
  }, [loadSessions, loadTemplates]);

  // Load session data when session changes
  useEffect(() => {
    if (currentSession?.id) {
      loadSessionData(currentSession.id);
    }
  }, [currentSession, loadSessionData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setFileInfo({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleString()
      });
      setError(null);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileInfo(null);
  };

  const handleUploadAndProcess = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setCsvErrors([]);

    try {
      // Create session
      const sessionResponse = await api.post(`${API_BASE_URL}/sessions`, null, {
        params: {
          filename: selectedFile.name,
          created_by_username: 'current_user'  // Replace with actual username
        }
      });

      const session = sessionResponse.data;

      // Upload CSV
      const formData = new FormData();
      formData.append('file', selectedFile);

      const uploadResponse = await api.post(
        `${API_BASE_URL}/sessions/${session.id}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { created_by_username: 'current_user' }
        }
      );

      // Generate emails
      await api.post(`${API_BASE_URL}/sessions/${session.id}/generate-emails`, null, {
        params: {
          sender_name: 'Matplast Sp. z o.o.',
          created_by_username: 'current_user'
        }
      });

      // Show warnings for partial errors
      if (uploadResponse.data.validation_errors?.length > 0) {
        setCsvErrors(uploadResponse.data.validation_errors);
      }

      setSuccess(uploadResponse.data.message);
      setCurrentSession(session);
      await loadSessions();
      await loadSessionData(session.id);
      
      // Clear file selection
      setSelectedFile(null);
      setFileInfo(null);
      
      // Switch to automation tab
      setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'automation'); return next; });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.validation_errors) {
        setError(detail.message);
        setCsvErrors(detail.validation_errors);
      } else {
        setError(typeof detail === 'string' ? detail : 'Nie udało się przetworzyć pliku');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewEmails = async () => {
    if (!currentSession) return;

    // If already showing previews, just toggle off
    if (showPreviews) {
      setShowPreviews(false);
      return;
    }

    // If we already have previews loaded, just show them
    if (emailPreviews.length > 0) {
      setShowPreviews(true);
      return;
    }

    // Otherwise, load previews from API
    setLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/sessions/${currentSession.id}/preview`);
      setEmailPreviews(response.data);
      setShowPreviews(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load email previews');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (!currentSession) return;

    const emailsToSend = selectMode && selectedEmailIds.size > 0 
      ? Array.from(selectedEmailIds)
      : null;
    
    const emailCount = emailsToSend 
      ? emailsToSend.length 
      : customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length;

    const confirmMessage = selectMode && selectedEmailIds.size > 0
      ? `Czy na pewno chcesz wysłać ${emailCount} wybranych e-maili?`
      : 'Czy na pewno chcesz wysłać wszystkie e-maile?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(
        `${API_BASE_URL}/sessions/${currentSession.id}/send-emails`,
        { dry_run: false, customer_email_ids: emailsToSend },
        { params: { created_by_username: 'current_user' } }
      );

      setSuccess(`Successfully sent ${response.data.total_sent} emails. Failed: ${response.data.total_failed}`);
      await loadSessionData(currentSession.id);
      
      // Clear selection after sending
      if (selectMode) {
        setSelectedEmailIds(new Set());
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckReplies = async () => {
    if (!currentSession) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(`${API_BASE_URL}/check-replies?days_back=7&session_id=${currentSession.id}`);
      setSuccess(`Sprawdzono odpowiedzi dla sesji. Zaktualizowano ${response.data.updated_count} rekordów.`);
      await loadSessionData(currentSession.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się sprawdzić odpowiedzi');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSingleEmail = async (emailId, client) => {
    if (!window.confirm(`Czy na pewno chcesz wysłać email do ${client}?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(
        `${API_BASE_URL}/emails/${emailId}/send`,
        null,
        { params: { dry_run: false } }
      );

      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
      await loadSessionData(currentSession.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się wysłać emaila');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetails = (customerEmail) => {
    setSelectedCustomerEmail(customerEmail);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedCustomerEmail(null);
  };

  const handleUpdateCustomerEmail = async (emailId, updates) => {
    try {
      await api.put(`${API_BASE_URL}/emails/${emailId}`, updates);
      await loadSessionData(currentSession.id);
      setSuccess('Zaktualizowano pomyślnie');
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zaktualizować');
    }
  };

  const handleCancelReplyTracking = async (emailId, client) => {
    if (!window.confirm(`Czy na pewno chcesz anulować oczekiwanie na odpowiedź od ${client}? Odpowiedzi nie będą już śledzone.`)) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`${API_BASE_URL}/emails/${emailId}/cancel`);
      setSuccess(`Anulowano oczekiwanie na odpowiedź od ${client}`);
      await loadSessionData(currentSession.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się anulować oczekiwania na odpowiedź');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNoteModal = (customerEmail) => {
    setEditingNoteEmail(customerEmail);
    setNoteModalValue(customerEmail.note || '');
    setShowNoteModal(true);
  };

  const handleCloseNoteModal = () => {
    setShowNoteModal(false);
    setEditingNoteEmail(null);
    setNoteModalValue('');
  };

  const handleSaveNote = async () => {
    if (!editingNoteEmail) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put(`${API_BASE_URL}/emails/${editingNoteEmail.id}`, { note: noteModalValue });
      setSuccess('Notatka została pomyślnie zapisana');
      await loadSessionData(currentSession.id);
      handleCloseNoteModal();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zapisać notatki');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDoNotSend = async (emailId, currentValue, client) => {
    const action = currentValue ? 'włączyć' : 'wyłączyć';
    if (!window.confirm(`Czy na pewno chcesz ${action} wysyłanie do ${client}?`)) {
      return;
    }

    try {
      await api.put(`${API_BASE_URL}/emails/${emailId}`, { do_not_send: !currentValue });
      setSuccess(`Status wysyłania zaktualizowany dla ${client}`);
      await loadSessionData(currentSession.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zaktualizować statusu');
    }
  };

  const handleExport = async (format = 'csv') => {
    if (!currentSession) return;

    try {
      const response = await api.get(
        `${API_BASE_URL}/sessions/${currentSession.id}/export`,
        {
          params: { format, include_status: true },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `session_${currentSession.id}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Nie udało się wyeksportować danych');
    }
  };

  const handleDeleteSession = async (sessionId, filename) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć sesję "${filename}"? Ta operacja jest nieodwracalna.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.delete(`${API_BASE_URL}/sessions/${sessionId}`);
      setSuccess(`Sesja "${filename}" została pomyślnie usunięta`);
      
      // If the deleted session was the current session, clear it
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setCustomerEmails([]);
        setEmailPreviews([]);
      }
      
      // Reload sessions list
      await loadSessions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się usunąć sesji');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = async (session) => {
    setCurrentSession(session);
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'automation'); return next; });
    setShowPreviews(false); // Reset preview visibility
    setEmailPreviews([]); // Clear old previews
    setExpandedCards(new Set()); // Reset expanded cards
    setSelectMode(false); // Reset select mode
    setSelectedEmailIds(new Set()); // Clear selections
    await loadSessionData(session.id);
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedEmailIds(new Set()); // Clear selections when toggling mode
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const pendingEmails = customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send);
    if (selectedEmailIds.size === pendingEmails.length) {
      // Deselect all
      setSelectedEmailIds(new Set());
    } else {
      // Select all pending
      setSelectedEmailIds(new Set(pendingEmails.map(e => e.id)));
    }
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const handleSaveTemplate = async (templateName, language, templateData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put(`${API_BASE_URL}/templates/${templateName}`, {
        language: language,
        columns: templateData.columns,
        subject: templateData.subject,
        body: templateData.body
      });

      setSuccess(`Zapisano szablon dla języka ${language}`);
      await loadTemplates();
      setEditingTemplate(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zapisać szablonu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplateLanguage = async (templateName, language) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć szablon dla języka ${language}?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.delete(`${API_BASE_URL}/templates/${templateName}/${language}`);
      setSuccess(`Szablon dla języka ${language} został pomyślnie usunięty`);
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się usunąć szablonu');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (templateName, language, templateData) => {
    setEditingTemplate({
      templateName,
      language,
      isNew: language === 'NEW',
      columns: templateData.columns || [],
      subject: templateData.subject || '',
      body: templateData.body || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'data':
        return (
          <div className="tab-content">
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}
            {csvErrors.length > 0 && (
              <div className="alert alert-error" style={{ textAlign: 'left' }}>
                <strong>Błędy walidacji CSV ({csvErrors.length}):</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {csvErrors.map((csvErr, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>{csvErr}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="tab-section">
              <h3>Import danych</h3>
              <p className="section-description">
                Prześlij plik CSV zawierający dane stojaków do przetworzenia i automatycznej dystrybucji e-maili.
              </p>

              <div className="csv-upload-section">
                <div className="file-upload-wrapper">
                  <label htmlFor="csv-upload" className="file-upload-label">
                    <div className="file-upload-icon">📁</div>
                    <div className="file-upload-text">
                      <strong>Kliknij, aby przesłać</strong> lub przeciągnij i upuść
                    </div>
                    <div className="file-upload-hint">Tylko pliki CSV (MAKS. 10MB)</div>
                  </label>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="file-upload-input"
                    disabled={loading}
                  />
                </div>

                {fileInfo && (
                  <div className="file-info-card">
                    <div className="file-info-header">
                      <div className="file-info-icon">📄</div>
                      <div className="file-info-details">
                        <h4>{fileInfo.name}</h4>
                        <p>
                          <span className="file-info-label">Size:</span> {fileInfo.size} • 
                          <span className="file-info-label"> Type:</span> {fileInfo.type || 'CSV'} • 
                          <span className="file-info-label"> Modified:</span> {fileInfo.lastModified}
                        </p>
                      </div>
                      <button onClick={handleFileRemove} className="btn-remove-file" disabled={loading}>
                        ✕
                      </button>
                    </div>
                    <div className="file-actions">
                      <button 
                        className="btn-primary" 
                        onClick={handleUploadAndProcess}
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : 'Upload & Process File'}
                      </button>
                    </div>
                  </div>
                )}

                {!fileInfo && (
                  <div className="upload-placeholder">
                    <p>Brak przesłanego pliku. Wybierz plik CSV, aby rozpocząć.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="tab-section">
              <h3>Ostatnie sesje</h3>
              {sessions.length > 0 ? (
                <div className="sessions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Nazwa pliku</th>
                        <th>Status</th>
                        <th>Klienci</th>
                        <th>Stojaki</th>
                        <th>Utworzono</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.slice(0, 10).map(session => (
                        <tr key={session.id} className={currentSession?.id === session.id ? 'selected' : ''}>
                          <td>{session.filename}</td>
                          <td><span className={`status-badge ${session.status.toLowerCase()}`}>{translateStatus(session.status)}</span></td>
                          <td>{session.total_customers}</td>
                          <td>{session.total_racks}</td>
                          <td>{new Date(session.created_at).toLocaleString()}</td>
                          <td>
                            <button 
                              className="btn-small" 
                              onClick={() => handleLoadSession(session)}
                              disabled={loading}
                            >
                              Wczytaj
                            </button>
                            <button 
                              className="btn-small" 
                              onClick={() => handleExport('csv')}
                              disabled={loading}
                            >
                              Eksportuj
                            </button>
                            <button 
                              className="btn-small btn-danger" 
                              onClick={() => handleDeleteSession(session.id, session.filename)}
                              disabled={loading}
                              title="Usuń sesję"
                            >
                              Usuń
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="placeholder-content">
                  <p>Brak sesji. Prześlij plik CSV, aby utworzyć pierwszą sesję.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'automation':
        return (
          <div className="tab-content">
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {!currentSession ? (
              <div className="placeholder-content">
                <p>Prześlij plik CSV w zakładce Dane, aby rozpocząć.</p>
              </div>
            ) : (
              <>
                <div className="tab-section">
                  <h3>Zarządzanie e-mailami - Sesja #{currentSession.id}</h3>
                  <p className="section-description">
                    Przeglądaj i wysyłaj e-maile do klientów. Status: <strong>{currentSession.status}</strong>
                  </p>

                  <div className="action-buttons">
                    <button 
                      className="btn-primary preview-toggle-btn" 
                      onClick={handlePreviewEmails}
                      disabled={loading}
                    >
                      <span className={`arrow-icon ${showPreviews ? 'rotated' : ''}`}>▼</span>
                      {showPreviews ? 'Ukryj podglądy' : 'Podgląd e-maili'}
                    </button>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={selectMode}
                          onChange={toggleSelectMode}
                          style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                        <span style={{ fontWeight: '500' }}>Wybierz ręcznie e-maile do wysłania</span>
                      </label>
                      
                      {selectMode && selectedEmailIds.size > 0 && (
                        <span style={{ 
                          padding: '4px 12px', 
                          backgroundColor: '#667eea', 
                          color: 'white', 
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          Wybrano: {selectedEmailIds.size}
                        </span>
                      )}
                    </div>

                    <button 
                      className="btn-success" 
                      onClick={handleSendEmails}
                      disabled={
                        loading || 
                        (selectMode && selectedEmailIds.size === 0) ||
                        (!selectMode && customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length === 0)
                      }
                    >
                      {loading ? 'Wysyłanie...' : selectMode 
                        ? `Wyślij wybrane e-maile (${selectedEmailIds.size})`
                        : `Wyślij wszystkie e-maile (${customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length})`
                      }
                    </button>
                    
                    <button 
                      className="btn-secondary" 
                      onClick={handleCheckReplies}
                      disabled={loading}
                    >
                      Sprawdź odpowiedzi
                    </button>
                  </div>

                  {/* Select Mode Help Text */}
                  {selectMode && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#e0e7ff',
                      border: '1px solid #c7d2fe',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      color: '#4338ca',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <strong>ℹ️ Tryb wyboru aktywny:</strong> Kliknij na wiersze w tabeli poniżej, aby wybrać e-maile do wysłania. Wybrane wiersze zostaną podświetlone na fioletowo.
                      </div>
                      <button
                        onClick={toggleSelectAll}
                        className="btn-small"
                        disabled={customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length === 0}
                        style={{ marginLeft: '10px', whiteSpace: 'nowrap' }}
                      >
                        {selectedEmailIds.size === customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length && selectedEmailIds.size > 0
                          ? '✕ Odznacz wszystkie'
                          : '✓ Zaznacz wszystkie'
                        }
                      </button>
                    </div>
                  )}

                  {/* Email Status Summary */}
                  {customerEmails.length > 0 && (
                    <div className="email-status-summary">
                      <div className="status-card status-card-total">
                        <div className="status-card-icon">📊</div>
                        <div className="status-card-content">
                          <div className="status-card-value">{customerEmails.length}</div>
                          <div className="status-card-label">Wszystkie E-maile</div>
                        </div>
                      </div>
                      <div className="status-card status-card-pending">
                        <div className="status-card-icon">⏳</div>
                        <div className="status-card-content">
                          <div className="status-card-value">{customerEmails.filter(e => e.status === 'PENDING' && !e.do_not_send).length}</div>
                          <div className="status-card-label">Oczekujące</div>
                        </div>
                      </div>
                      <div className="status-card status-card-sent">
                        <div className="status-card-icon">✉️</div>
                        <div className="status-card-content">
                          <div className="status-card-value">{customerEmails.filter(e => e.status === 'SENT').length}</div>
                          <div className="status-card-label">Wysłane</div>
                        </div>
                      </div>
                      <div className="status-card status-card-failed">
                        <div className="status-card-icon">❌</div>
                        <div className="status-card-content">
                          <div className="status-card-value">{customerEmails.filter(e => e.status === 'FAILED').length}</div>
                          <div className="status-card-label">Niepowodzenie</div>
                        </div>
                      </div>
                      <div className="status-card status-card-replied">
                        <div className="status-card-icon">✅</div>
                        <div className="status-card-content">
                          <div className="status-card-value">{customerEmails.filter(e => e.status === 'REPLIED').length}</div>
                          <div className="status-card-label">Odpowiedziano</div>
                        </div>
                      </div>

                    </div>
                  )}
                </div>

                {showPreviews && emailPreviews.length > 0 && (
                  <div className="tab-section email-previews-section">
                    <h3>Podgląd e-maili</h3>
                    {emailPreviews.slice(0, 3).map(preview => {
                      const isExpanded = expandedCards.has(preview.customer_email_id);
                      return (
                        <div 
                          key={preview.customer_email_id} 
                          className={`email-preview-accordion ${isExpanded ? 'expanded' : ''}`}
                        >
                          <div 
                            className="accordion-header"
                            onClick={() => toggleCardExpansion(preview.customer_email_id)}
                          >
                            <span className="accordion-title">
                              Do: {preview.email} ({preview.client})
                            </span>
                            <span className={`accordion-arrow ${isExpanded ? 'rotated' : ''}`}>▼</span>
                          </div>
                          <div className={`accordion-content ${isExpanded ? 'open' : ''}`}>
                            <div className="accordion-content-inner">
                              <p><strong>Temat:</strong> {preview.subject}</p>
                              <p><strong>Język:</strong> {preview.language}</p>
                              <p><strong>Liczba stojaków:</strong> {preview.rack_count}</p>
                              <details className="email-body-details">
                                <summary>Wyświetl treść e-maila</summary>
                                <div className="email-body-content" dangerouslySetInnerHTML={{ __html: preview.body }} />
                              </details>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="tab-section">
                  <h3>Customer Emails ({customerEmails.length})</h3>
                  {customerEmails.length > 0 ? (
                    <div className="customers-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Klient</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Stojaki</th>
                            <th>Wysłano</th>
                            <th>Odpowiedź</th>
                            <th>Załącznik</th>
                            <th>Gotowe</th>
                            <th>Notatka</th>
                            <th>Akcje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerEmails.map(ce => {
                            const isSelected = selectedEmailIds.has(ce.id);
                            const isSelectable = selectMode && ce.status === 'PENDING' && !ce.do_not_send;
                            
                            return (
                            <tr 
                              key={ce.id} 
                              className={`${ce.do_not_send ? 'do-not-send-row' : ''} ${isSelected ? 'selected-row' : ''} ${isSelectable ? 'selectable-row' : ''}`}
                              onClick={(e) => {
                                // Only toggle selection if in select mode and row is selectable
                                // Don't toggle if clicking on buttons or interactive elements
                                if (isSelectable && !e.target.closest('button, input[type="checkbox"], .note-cell')) {
                                  toggleEmailSelection(ce.id);
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (isSelectable && !isSelected) {
                                  e.currentTarget.style.backgroundColor = '#ede9fe';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isSelectable && !isSelected) {
                                  e.currentTarget.style.backgroundColor = '';
                                }
                              }}
                              style={{
                                cursor: isSelectable ? 'pointer' : 'default',
                                backgroundColor: isSelected ? '#d8b4fe' : undefined,
                                transition: 'background-color 0.15s ease'
                              }}
                            >
                              <td>{ce.client}</td>
                              <td>{ce.email}</td>
                              <td>
                                <span className={`status-badge ${ce.status.toLowerCase()}`}>{translateStatus(ce.status)}</span>
                                {ce.do_not_send && <span className="do-not-send-badge">Nie wysyłaj</span>}
                              </td>
                              <td>{ce.rack_count}</td>
                              <td>{ce.sent_at ? new Date(ce.sent_at).toLocaleDateString() : '-'}</td>
                              <td>
                                {ce.has_reply ? (
                                  <span style={{ fontSize: '20px' }} title="Klient odpowiedział">✅</span>
                                ) : ce.sent_at ? (
                                  <span style={{ color: '#999' }}>-</span>
                                ) : (
                                  <span style={{ color: '#999' }}>-</span>
                                )}
                              </td>
                              <td>
                                {ce.has_attachment ? (
                                  <span 
                                    style={{ fontSize: '20px', cursor: 'pointer' }} 
                                    title={`Klient wysłał zdjęcie(a): ${ce.attachment_info ? JSON.parse(ce.attachment_info).map(a => a.filename).join(', ') : 'załącznik graficzny'}`}
                                  >
                                    📷
                                  </span>
                                ) : ce.has_reply ? (
                                  <span style={{ fontSize: '20px' }} title="Klient odpowiedział bez zdjęcia">❌</span>
                                ) : (
                                  <span style={{ color: '#999' }}>-</span>
                                )}
                              </td>
                              <td>
                                <input 
                                  type="checkbox" 
                                  checked={ce.racks_ready || false}
                                  onChange={(e) => handleUpdateCustomerEmail(ce.id, { racks_ready: e.target.checked })}
                                  disabled={loading}
                                  title="Oznacz stojaki jako gotowe do odbioru"
                                />
                              </td>
                              <td>
                                <div 
                                  className="note-cell"
                                  onClick={() => handleOpenNoteModal(ce)}
                                  title="Kliknij, aby edytować notatkę"
                                >
                                  {ce.note ? (
                                    <span className="note-text">{ce.note}</span>
                                  ) : (
                                    <span className="note-placeholder">Dodaj notatkę...</span>
                                  )}
                                  <span className="note-edit-icon">✏️</span>
                                </div>
                              </td>
                              <td>
                                {ce.status === 'PENDING' && (
                                  <>
                                    {!selectMode && (
                                      <button 
                                        className="btn-small btn-success" 
                                        onClick={() => handleSendSingleEmail(ce.id, ce.client)}
                                        disabled={loading || ce.do_not_send}
                                        title={ce.do_not_send ? "Email oznaczony jako nie wysyłaj" : "Wyślij e-mail do tego klienta"}
                                      >
                                        Wyślij
                                      </button>
                                    )}
                                    <button 
                                      className={`btn-small ${ce.do_not_send ? 'btn-warning' : 'btn-secondary'}`}
                                      onClick={() => handleToggleDoNotSend(ce.id, ce.do_not_send, ce.client)}
                                      disabled={loading}
                                      title={ce.do_not_send ? "Włącz wysyłanie" : "Nie wysyłaj tego emaila"}
                                    >
                                      {ce.do_not_send ? '✓ Włącz' : 'Nie wysyłaj'}
                                    </button>
                                  </>
                                )}
                                {ce.status === 'SENT' && !ce.has_reply && (
                                  <button 
                                    className="btn-small btn-warning" 
                                    onClick={() => handleCancelReplyTracking(ce.id, ce.client)}
                                    disabled={loading}
                                    title="Anuluj oczekiwanie na odpowiedź"
                                  >
                                    Anuluj
                                  </button>
                                )}
                                <button 
                                  className="btn-small" 
                                  onClick={() => handleShowDetails(ce)}
                                  disabled={loading}
                                  title="Pokaż szczegóły"
                                >
                                  Szczegóły
                                </button>
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="placeholder-content">
                      <p>Nie znaleziono e-maili od klientów.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );

      case 'settings':
        return (
          <div className="tab-content">
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div className="tab-section">
              <h3>Ustawienia szablonów e-mail</h3>
              <p className="section-description">
                Zarządzaj szablonami e-mail dla różnych języków. Możesz edytować istniejące szablony lub dodać nowe wersje językowe.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ marginRight: '10px' }}><strong>Szablon:</strong></label>
                <select 
                  value={selectedTemplate} 
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  style={{ padding: '8px', marginRight: '20px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  {Object.keys(templates).map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>

                <button 
                  className="btn-primary"
                  onClick={() => handleEditTemplate(selectedTemplate, 'NEW', { columns: [], subject: '', body: '' })}
                  disabled={loading}
                >
                  ➕ Dodaj nowy język
                </button>
              </div>

              {/* Available Languages */}
              {templates[selectedTemplate]?.languages && (
                <div className="tab-section">
                  <h4>Dostępne języki</h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {Object.keys(templates[selectedTemplate].languages).map(lang => (
                      <div key={lang} style={{ 
                        padding: '10px 15px', 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        backgroundColor: 'white'
                      }}>
                        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{lang}</span>
                        <button 
                          className="btn-small"
                          onClick={() => handleEditTemplate(selectedTemplate, lang, templates[selectedTemplate].languages[lang])}
                          disabled={loading}
                        >
                          Edytuj
                        </button>
                        <button 
                          className="btn-small btn-danger"
                          onClick={() => handleDeleteTemplateLanguage(selectedTemplate, lang)}
                          disabled={loading || Object.keys(templates[selectedTemplate].languages).length === 1}
                          title={Object.keys(templates[selectedTemplate].languages).length === 1 ? "Nie można usunąć ostatniego języka" : "Usuń język"}
                        >
                          Usuń
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Edit Form */}
              {editingTemplate && (
                <div className="tab-section" style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                  <h4>
                    {editingTemplate.isNew ? 'Dodaj nowy szablon językowy' : `Edytuj szablon ${editingTemplate.language}`}
                  </h4>

                  <div style={{ marginBottom: '20px' }}>
                    {editingTemplate.isNew && (
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                          Kod języka (np. EN, PL, FR):
                        </label>
                        <input
                          type="text"
                          value={editingTemplate.language === 'NEW' ? '' : editingTemplate.language}
                          onChange={(e) => setEditingTemplate({...editingTemplate, language: e.target.value.toUpperCase()})}
                          placeholder="EN"
                          maxLength={2}
                          style={{ padding: '8px', width: '100px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </div>
                    )}

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Kolumny tabeli (jedna na linię):
                      </label>
                      <textarea
                        value={editingTemplate.columns.join('\n')}
                        onChange={(e) => setEditingTemplate({
                          ...editingTemplate, 
                          columns: e.target.value.split('\n').filter(c => c.trim())
                        })}
                        rows={4}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '4px', 
                          border: '1px solid #ddd',
                          fontFamily: 'monospace'
                        }}
                        placeholder="Gestellnummer&#10;Kunde&#10;Adresse&#10;Auftrag"
                      />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Temat e-maila:
                      </label>
                      <input
                        type="text"
                        value={editingTemplate.subject}
                        onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '4px', 
                          border: '1px solid #ddd'
                        }}
                        placeholder="Temat wiadomości e-mail."
                      />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Szablon treści e-maila:
                      </label>
                      <textarea
                        value={editingTemplate.body}
                        onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                        rows={12}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '4px', 
                          border: '1px solid #ddd',
                          fontFamily: 'monospace',
                          fontSize: '14px'
                        }}
                        placeholder="Użyj {{orders_table}} dla tabeli i {{sender_name}} dla nadawcy. Proszę pamiętać aby je dodać (wzór dla DE)."
                      />
                      <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Dostępne zmienne: {'{{orders_table}}'}, {'{{sender_name}}'}
                      </small>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className="btn-success"
                        onClick={() => handleSaveTemplate(
                          editingTemplate.templateName, 
                          editingTemplate.language,
                          editingTemplate
                        )}
                        disabled={loading || !editingTemplate.subject || !editingTemplate.body || editingTemplate.language.length !== 2 || editingTemplate.columns.length === 0}
                      >
                        {loading ? 'Zapisywanie...' : 'Zapisz szablon'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="stojaki-page">
    <div className="page-container">
      <div className="page-header">
        <div className="breadcrumb-nav">
          <Link to="/logistics" className="breadcrumb-link">
            <Truck className="breadcrumb-icon" aria-hidden="true" /> Logistyka
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Stojaki Auto-Email</span>
        </div>
        <h2 className="stojaki-page-title"><MailCheck aria-hidden="true" /> Stojaki Auto-Email</h2>
        <p>System zarządzania automatycznymi e-mailami</p>
      </div>

      <div className="stojaki-tabs-wrapper">
        <div className="stojaki-tabs">
          <button
            className={`tab-btn ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'data'); return next; })}
          >
            Dane
          </button>
          <button
            className={`tab-btn ${activeTab === 'automation' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'automation'); return next; })}
          >
            Automatyzacja
          </button>
          <button
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'settings'); return next; })}
          >
            Ustawienia
          </button>
        </div>
      </div>

      <div className="page-content">
        {renderTabContent()}
      </div>

      {/* Note Edit Modal */}
      {showNoteModal && editingNoteEmail && (
        <div className="modal-overlay" onClick={handleCloseNoteModal}>
          <div className="modal-content modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Edytuj notatkę</h3>
              <button className="modal-close" onClick={handleCloseNoteModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <p style={{ marginBottom: '10px', color: '#4a5568' }}>
                  <strong>Klient:</strong> {editingNoteEmail.client}
                </p>
                <p style={{ marginBottom: '10px', color: '#4a5568' }}>
                  <strong>Email:</strong> {editingNoteEmail.email}
                </p>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#2d3748' }}>
                  Notatka:
                </label>
                <textarea
                  value={noteModalValue}
                  onChange={(e) => setNoteModalValue(e.target.value)}
                  placeholder="Wpisz swoją notatkę tutaj..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={handleCloseNoteModal}
                disabled={loading}
              >
                Anuluj
              </button>
              <button 
                className="btn-success" 
                onClick={handleSaveNote}
                disabled={loading}
              >
                {loading ? 'Zapisywanie...' : '✓ Zapisz notatkę'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v>

      {/* Details Modal */}
      {showDetailsModal && selectedCustomerEmail && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📧 Szczegóły e-maila klienta</h3>
              <button className="modal-close" onClick={handleCloseDetails}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Informacje o kliencie</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Klient:</span>
                    <span className="detail-value">{selectedCustomerEmail.client}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedCustomerEmail.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Język:</span>
                    <span className="detail-value">{selectedCustomerEmail.language}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${selectedCustomerEmail.status.toLowerCase()}`}>
                      {translateStatus(selectedCustomerEmail.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Stojaki:</span>
                    <span className="detail-value">{selectedCustomerEmail.rack_count} ({selectedCustomerEmail.rack_ids})</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Wysłano:</span>
                    <span className="detail-value">
                      {selectedCustomerEmail.sent_at ? new Date(selectedCustomerEmail.sent_at).toLocaleString() : 'Nie wysłano'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedCustomerEmail.has_reply && (
                <div className="detail-section">
                  <h4>Odpowiedź klienta {selectedCustomerEmail.has_attachment && '📷'}</h4>
                  <div className="detail-item">
                    <span className="detail-label">Otrzymano:</span>
                    <span className="detail-value">
                      {selectedCustomerEmail.reply_received_at 
                        ? new Date(selectedCustomerEmail.reply_received_at).toLocaleString() 
                        : '-'}
                    </span>
                  </div>
                  {selectedCustomerEmail.has_attachment && selectedCustomerEmail.attachment_info && (
                    <div className="detail-item">
                      <span className="detail-label">Załączniki:</span>
                      <div className="attachment-list">
                        {JSON.parse(selectedCustomerEmail.attachment_info).map((att, idx) => (
                          <div key={idx} className="attachment-item">
                            📎 {att.filename} <span className="attachment-type">({att.content_type})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Wiadomość:</span>
                    <div className="reply-message">
                      {selectedCustomerEmail.reply_content || 'Brak treści wiadomości'}
                    </div>
                  </div>
                </div>
              )}

              {(!selectedCustomerEmail.has_reply && selectedCustomerEmail.sent_at) && (
                <div className="detail-section">
                  <p className="no-reply-message">⏳ Oczekiwanie na odpowiedź klienta...</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDetails}>Zamknij</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default StojkiAutoEmail;
